/**
 * WHICH REASONS A DISCARD SHEET MAY OFFER, and the words for the one of them that is not a discard.
 *
 * Four of the five say the row was never a person: a test, spam, a duplicate, something else. The
 * fifth, a privacy request, says the row IS a person and she has asked to stop being one here, and
 * exactly one door in this product can honour that. The enquiry is blanked, soft deleted, every
 * audit image of it replaced by a redaction image, and the website told `forget`. The call log, the
 * expense book and the task list share the same control and erase nothing at all, so the server
 * refuses the word at all three (`NON_ERASING_DISCARD_REASONS` in `packages/shared/src/enums.ts`).
 *
 * SO THIS IS THE INTERFACE HALF OF A REFUSAL THE SERVER ALREADY MAKES, and never the control
 * itself: CLAUDE.md binding rule 6 says hiding something on a screen is UX and the server is the
 * check. Offering a word that comes back 422 is a dead control, and a dead control on this
 * particular sheet is worse than most, because the person pressing it believes she has just
 * honoured a request from somebody who asked to be forgotten.
 */
import {
  DISCARD_REASONS,
  NON_ERASING_DISCARD_REASONS,
  PHONE_FORGET_OUTCOMES,
  type DiscardReason,
  type PhoneForgetOutcome,
} from '@fk/shared';

/** The one reason that reaches back into what is already written. */
export function erasesThePerson(reason: DiscardReason): boolean {
  return reason === 'privacy_request';
}

/** Five where the door can actually erase somebody, four where it cannot. */
export function reasonsOffered(canForget: boolean): readonly DiscardReason[] {
  return canForget ? DISCARD_REASONS : NON_ERASING_DISCARD_REASONS;
}

/**
 * The confirming button. "Discard this enquiry" is the wrong sentence for the one reason that is
 * not a discard, so the button says the thing the server is about to do instead.
 */
export function actionWords(reason: DiscardReason, what: string): string {
  return `${erasesThePerson(reason) ? 'Forget' : 'Discard'} ${what}`;
}

/**
 * What the screen says afterwards. The four keep the sentence the screen already had. A forget adds
 * what the server answered about the phone system's copies of her texts (ops #27), and nothing when
 * it said nothing, so the sentence never promises an erasure nobody asked for.
 */
export function doneWords(
  reason: DiscardReason,
  discarded: string,
  phoneSystem?: PhoneForgetOutcome,
): string {
  if (!erasesThePerson(reason)) return discarded;
  const said = phoneSystem === undefined ? '' : PHONE_SYSTEM_SAID[phoneSystem];
  return said === '' ? FORGOTTEN_SENTENCE : `${FORGOTTEN_SENTENCE} ${said}`;
}

/**
 * And it deliberately does not say "the record and the reason stay", which is what a discard says
 * and is the opposite of what happened here.
 */
export const FORGOTTEN_SENTENCE =
  'Forgotten. Their name and their details are gone, here and in the record of what was written.';

/**
 * WHAT THE PHONE SYSTEM WAS TOLD, in the server's word (ops #27). It erases everything at a number,
 * so a number a client contact or another enquiry still holds is not asked about, and the office
 * learns it here and nowhere else: no screen reads the erasure request that records it.
 */
const PHONE_SYSTEM_SAID: Record<PhoneForgetOutcome, string> = {
  asked: 'The phone system is asked to erase its copies of their texts and calls.',
  held_elsewhere:
    "The phone system was not asked to erase its copies of their texts and calls, because a client contact or another enquiry still holds their number, and it would erase the other person's texts and calls along with the forgotten one's.",
  no_number: '',
};

/** The server's word about the phone system in a forget's answer, or nothing when it gave none. */
export function phoneSystemIn(answer: unknown): PhoneForgetOutcome | undefined {
  if (answer === null || typeof answer !== 'object') return undefined;
  const said = (answer as { phoneSystem?: unknown }).phoneSystem;
  return (PHONE_FORGET_OUTCOMES as readonly unknown[]).includes(said)
    ? (said as PhoneForgetOutcome)
    : undefined;
}

/**
 * FORGETTING A HOUSEHOLD (ops #82, `docs/plan-household-forget.md` section 8): what the step says
 * before the owner types the household's name, every clause a thing that cannot be taken back, and
 * what stays, as the owner decided on 2026-10-22: the money records as the business's tax records,
 * the PDFs as issued, and the opt-outs on their numbers.
 */
export const HOUSEHOLD_FORGET_WARNING =
  "Their name, their numbers and addresses, their homes' addresses and codes, their texts, emails " +
  'and calls, and everything written about them go, here and in the record of what was written. ' +
  'The website and home are told to forget them too. What stays is their invoices, receipts and ' +
  "payments, kept as the business's tax records under the name Forgotten, with the PDFs exactly as " +
  'they were issued; and the opt-outs on their numbers, kept so nobody contacts them again. None of ' +
  "it can be undone. Type the household's name to confirm.";

/** What the page says once a household is forgotten, on the client list it goes to. */
export const HOUSEHOLD_FORGOTTEN_SENTENCE =
  'Forgotten. Their invoices, receipts and payments stay, under the name Forgotten.';

/**
 * THE ENQUIRIES THAT ONLY SHARE A NUMBER OF THEIRS (ops #82, decision A): the forget leaves them,
 * their number unmarked and not asked of the phone system, for an owner to review each by hand.
 */
export function sharingWords(count: number): string {
  return count === 1
    ? '1 other enquiry shares a number this household used. Review it by hand:'
    : `${count} other enquiries share a number this household used. Review each by hand:`;
}

/** Said while the rest of their record is still to clear, which the documents job finishes. */
export const HOUSEHOLD_REST_CLEARS = 'The rest of their record clears within a few minutes.';

/**
 * The done sentence of a household's forget: what stays, what the phone system is asked about their
 * texts in the server's word, as `doneWords` says it for an enquiry, and that the rest clears in
 * minutes. Asked, never said erased: the phone system's answer, calls erased and held, comes after
 * the press, and the owners' read of the forget says it (faithfully-kept-phone#33).
 */
export function householdForgottenWords(phoneSystem?: PhoneForgetOutcome): string {
  const said = phoneSystem === undefined ? '' : PHONE_SYSTEM_SAID[phoneSystem];
  return [HOUSEHOLD_FORGOTTEN_SENTENCE, said, HOUSEHOLD_REST_CLEARS]
    .filter((part) => part !== '')
    .join(' ');
}

/**
 * Said before the confirming press, because every clause of it is a thing that cannot be taken
 * back. The last clause is why the notes box is not drawn: the server drops typed words on a
 * privacy request, since a sentence written in good faith beside an erasure is the likeliest way
 * for the erased person to survive her own erasure.
 *
 * AND WHAT BECOMES OF HER TEXTS (ops #27): blanked here, the phone system asked to erase its own
 * copies unless another record still holds her number, and the consent and stop records kept whole,
 * number and quoted words, because the owner decided on 2026-09-17 (D10) that they are the proof and
 * a number is kept to be kept away from. Whether the phone system was asked is not known before the
 * press, so this says the condition, and `doneWords` says afterwards which it was.
 */
export const FORGET_WARNING =
  'Their name, their number and anything written about them go, here and in the record of what was ' +
  'written. What stays is that an enquiry arrived, when it did, and where it came from. The ' +
  'website is told to forget them too, where it sent this one. Their texts are blanked. The phone ' +
  'system is asked to erase its copies of their texts and calls, unless a client contact or another ' +
  'enquiry still holds their number, which is said once they are forgotten. The consent and stop ' +
  'records keep their number and the words they quote, as proof. Nothing typed is kept with it, and ' +
  'none of it can be undone.';
